import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Image, Search, Trash2, Download, Upload, Check, X, GripVertical,
  ChevronDown, RefreshCw, CheckSquare, Square, Plus, ArrowUpDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GalleryManagementTab = ({ authHeaders, flash }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState('upload'); // 'upload' | 'url'
  const [newUrls, setNewUrls] = useState('');
  const [adding, setAdding] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const fileInputRef = useRef(null);
  const imgRefs = useRef({});

  const loadGallery = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/gallery`, { headers: authHeaders() });
      setImages(res.data);
    } catch {
      flash('Failed to load gallery', true);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, flash]);

  useEffect(() => { loadGallery(); }, [loadGallery]);

  const filtered = images.filter(img =>
    !search ||
    (img.url || '').toLowerCase().includes(search.toLowerCase()) ||
    (img.caption || '').toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(i => i.id)));
    }
  };

  const handleAddImages = async () => {
    const urls = newUrls.split('\n').map(u => u.trim()).filter(Boolean);
    if (urls.length === 0) return;
    setAdding(true);
    try {
      await axios.post(`${BACKEND_URL}/api/portal/admin/gallery`, { urls }, { headers: authHeaders() });
      flash(`${urls.length} image(s) added`);
      setNewUrls('');
      setShowAddModal(false);
      loadGallery();
    } catch {
      flash('Failed to add images', true);
    } finally {
      setAdding(false);
    }
  };

  const handleFileUpload = async () => {
    if (uploadFiles.length === 0) return;
    setAdding(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      uploadFiles.forEach(f => formData.append('files', f));
      await axios.post(`${BACKEND_URL}/api/portal/admin/gallery/upload`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      flash(`${uploadFiles.length} image(s) uploaded`);
      setUploadFiles([]);
      setUploadProgress(0);
      setShowAddModal(false);
      loadGallery();
    } catch {
      flash('Failed to upload images', true);
    } finally {
      setAdding(false);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = [...e.dataTransfer.files].filter(f => f.type.startsWith('image/'));
    if (files.length) setUploadFiles(prev => [...prev, ...files]);
  };

  const removeUploadFile = (idx) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDelete = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} image(s)?`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/portal/admin/gallery`, {
        headers: authHeaders(),
        data: { ids },
      });
      flash(`${ids.length} image(s) removed`);
      setSelected(new Set());
      loadGallery();
    } catch {
      flash('Failed to delete', true);
    } finally {
      setDeleting(false);
    }
  };

  const downloadSelected = () => {
    const items = images.filter(i => selected.has(i.id));
    items.forEach(img => {
      const a = document.createElement('a');
      a.href = img.url;
      a.download = img.url.split('/').pop() || 'image';
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    });
  };

  /* ── Drag & Drop reorder ── */
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = async () => {
    if (dragIdx === null || dragOverIdx === null || dragIdx === dragOverIdx) {
      setDragIdx(null);
      setDragOverIdx(null);
      return;
    }
    const reordered = [...filtered];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dragOverIdx, 0, moved);
    const orderPayload = reordered.map((img, i) => ({ id: img.id, order: i + 1 }));
    setDragIdx(null);
    setDragOverIdx(null);
    // Optimistic update
    setImages(reordered.map((img, i) => ({ ...img, order: i + 1 })));
    try {
      await axios.put(`${BACKEND_URL}/api/portal/admin/gallery/reorder`, { order: orderPayload }, { headers: authHeaders() });
      flash('Order saved');
    } catch {
      flash('Failed to save order', true);
      loadGallery();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <RefreshCw size={20} className="animate-spin mr-2" /> Loading gallery...
      </div>
    );
  }

  return (
    <div data-testid="gallery-management-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-gray-900">Gallery Management</h2>
          <p className="text-sm text-gray-500 mt-1">{images.length} images · Drag to reorder · Click to select</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selected.size > 0 && (
            <>
              <button
                onClick={downloadSelected}
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:border-gray-300 transition-all"
                data-testid="download-selected-btn"
              >
                <Download size={12} /> Download ({selected.size})
              </button>
              <button
                onClick={() => handleDelete([...selected])}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-2 border border-red-200 rounded-lg text-xs font-bold text-red-600 hover:bg-red-50 transition-all"
                data-testid="delete-selected-btn"
              >
                <Trash2 size={12} /> Delete ({selected.size})
              </button>
            </>
          )}
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white rounded-lg text-xs font-bold hover:bg-gray-800 transition-all"
            data-testid="add-images-btn"
          >
            <Plus size={12} /> Add Images
          </button>
        </div>
      </div>

      {/* Search + Select All */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-900 focus:outline-none focus:border-orange-300"
            placeholder="Search by URL or caption..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="gallery-search"
          />
        </div>
        <button
          onClick={toggleAll}
          className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-xs font-bold text-gray-500 hover:border-gray-300 transition-all"
          data-testid="select-all-btn"
        >
          {selected.size === filtered.length && filtered.length > 0 ? <CheckSquare size={13} /> : <Square size={13} />}
          {selected.size > 0 ? `${selected.size} selected` : 'Select All'}
        </button>
        <div className="flex items-center gap-1 text-[10px] text-gray-400 font-bold">
          <ArrowUpDown size={10} /> Drag to sort
        </div>
      </div>

      {/* Image Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
          <Image size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm font-bold text-gray-400">
            {images.length === 0 ? 'No gallery images yet' : 'No images match your search'}
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-3 text-xs font-bold text-orange-600 hover:underline"
          >
            + Add your first images
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2" data-testid="gallery-grid">
          {filtered.map((img, idx) => {
            const isSelected = selected.has(img.id);
            const isDragging = dragIdx === idx;
            const isDragOver = dragOverIdx === idx;
            return (
              <div
                key={img.id}
                ref={el => imgRefs.current[img.id] = el}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragEnd={handleDragEnd}
                onClick={() => toggleSelect(img.id)}
                className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer group border-2 transition-all ${
                  isSelected ? 'border-orange-500 ring-2 ring-orange-200' :
                  isDragOver ? 'border-blue-400 ring-2 ring-blue-100' :
                  isDragging ? 'opacity-40 border-gray-200' :
                  'border-transparent hover:border-gray-300'
                }`}
                data-testid={`gallery-item-${img.id}`}
              >
                <img
                  src={img.url}
                  alt={img.caption || ''}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Selection overlay */}
                <div className={`absolute inset-0 transition-opacity ${isSelected ? 'bg-orange-500/20' : 'bg-black/0 group-hover:bg-black/10'}`} />
                {/* Checkbox */}
                <div className={`absolute top-1.5 left-1.5 w-5 h-5 rounded flex items-center justify-center transition-all ${
                  isSelected ? 'bg-orange-500 text-white' : 'bg-white/80 text-gray-400 opacity-0 group-hover:opacity-100'
                }`}>
                  {isSelected ? <Check size={10} strokeWidth={3} /> : <Square size={10} />}
                </div>
                {/* Drag handle */}
                <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-80 text-white drop-shadow-md">
                  <GripVertical size={14} />
                </div>
                {/* Order number */}
                <div className="absolute bottom-1 right-1.5 text-[8px] font-bold text-white/80 bg-black/40 px-1 rounded">
                  {img.order || idx + 1}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Images Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowAddModal(false)} onKeyDown={e => e.key === 'Escape' && setShowAddModal(false)} tabIndex={-1} ref={el => el && el.focus()}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4" onClick={e => e.stopPropagation()} data-testid="add-images-modal">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-black text-gray-900">Add Gallery Images</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>

            {/* Tabs: Upload / URL */}
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setAddMode('upload')}
                className={`flex-1 py-2.5 text-xs font-bold transition-all ${addMode === 'upload' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-400 hover:text-gray-600'}`}
                data-testid="add-mode-upload"
              >
                <Upload size={12} className="inline mr-1.5" />Upload Files
              </button>
              <button
                onClick={() => setAddMode('url')}
                className={`flex-1 py-2.5 text-xs font-bold transition-all ${addMode === 'url' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-400 hover:text-gray-600'}`}
                data-testid="add-mode-url"
              >
                <Image size={12} className="inline mr-1.5" />Paste URLs
              </button>
            </div>

            <div className="px-5 py-4">
              {addMode === 'upload' ? (
                <>
                  {/* Drop zone */}
                  <div
                    onDrop={handleFileDrop}
                    onDragOver={e => e.preventDefault()}
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-orange-300 transition-colors"
                    data-testid="upload-drop-zone"
                  >
                    <Upload size={28} className="mx-auto mb-2 text-gray-300" />
                    <p className="text-xs font-bold text-gray-500">Drag & drop images here</p>
                    <p className="text-[10px] text-gray-400 mt-1">or click to browse · JPG, PNG, WebP, GIF · Max 10MB each</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={e => {
                        const files = [...e.target.files].filter(f => f.type.startsWith('image/'));
                        if (files.length) setUploadFiles(prev => [...prev, ...files]);
                        e.target.value = '';
                      }}
                      data-testid="upload-file-input"
                    />
                  </div>

                  {/* File preview list */}
                  {uploadFiles.length > 0 && (
                    <div className="mt-3 max-h-40 overflow-y-auto space-y-1.5" data-testid="upload-file-list">
                      {uploadFiles.map((f, i) => (
                        <div key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
                          <img
                            src={URL.createObjectURL(f)}
                            alt={f.name}
                            className="w-8 h-8 object-cover rounded"
                          />
                          <span className="text-xs text-gray-700 flex-1 truncate">{f.name}</span>
                          <span className="text-[10px] text-gray-400">{(f.size / 1024).toFixed(0)} KB</span>
                          <button onClick={() => removeUploadFile(i)} className="text-gray-400 hover:text-red-500 transition-colors">
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      <p className="text-[10px] text-gray-400 pt-1">{uploadFiles.length} file(s) ready to upload</p>
                    </div>
                  )}

                  {/* Upload progress */}
                  {adding && uploadProgress > 0 && (
                    <div className="mt-3">
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%`, backgroundColor: '#E8520A' }} />
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1 text-center">{uploadProgress}%</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Image URLs (one per line)</label>
                  <textarea
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-xs text-gray-900 font-mono focus:outline-none focus:border-orange-300 resize-y"
                    rows={8}
                    value={newUrls}
                    onChange={e => setNewUrls(e.target.value)}
                    placeholder={"https://example.com/image1.jpg\nhttps://example.com/image2.jpg"}
                    data-testid="new-urls-input"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    {newUrls.split('\n').filter(u => u.trim()).length} URL(s) ready to add
                  </p>
                </>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-xs font-bold text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addMode === 'upload' ? handleFileUpload : handleAddImages}
                disabled={adding || (addMode === 'upload' ? uploadFiles.length === 0 : !newUrls.trim())}
                className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white rounded-lg text-xs font-bold hover:bg-gray-800 transition-all disabled:opacity-50"
                data-testid="confirm-add-btn"
              >
                {adding ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
                {adding ? (addMode === 'upload' ? 'Uploading...' : 'Adding...') : (addMode === 'upload' ? `Upload ${uploadFiles.length} Image(s)` : 'Add Images')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryManagementTab;
